import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '@/components/layouts/AppLayout'
import { SEOHead } from '@/components/ui/SEOHead'
import { useAuthGuard } from '@/hooks/useAuthGuard'
import { ShieldCheck, Info } from '@phosphor-icons/react'
import type { User, Group } from '@/types/database.types'
import { Abacus, calculateUnpaidDebts, type DashboardRates, type AgendaWidgetData, type UpcomingMeetingData } from '@/utils/abacus'
import { Skeleton } from '@/components/ui/skeleton'
import BottomNav from '@/components/BottomNav'
import { MarketList } from '@/components/dashboard/MarketList'
import { WidgetSelector } from '@/components/dashboard/WidgetSelector'
import { formatFullName } from '@/utils/stringUtils'
import { getPreferences, savePreferences } from '@/services/userService'
import { ProfileCompletionModal } from '@/components/profile/ProfileCompletionModal'

import { UserAvatar } from '@/components/common/UserAvatar'
import { listVariants, itemVariants } from '@/themes/animations'
import { DashboardHeader } from '@/components/dashboard/DashboardHeader'
import { MissingIbanAlert } from '@/components/dashboard/MissingIbanAlert'
import { AgendaWidget } from '@/components/dashboard/AgendaWidget'
import { UnpaidDebtsWidget } from '@/components/dashboard/UnpaidDebtsWidget'
import { PendingTransactionsWidget } from '@/components/dashboard/PendingTransactionsWidget'
import { ActiveGroupsWidget } from '@/components/dashboard/ActiveGroupsWidget'
import AdBanner from '@/components/ads/AdBanner'
import { logError } from '@/lib/log'

export default function DashboardPage() {
  const { user: authUser, loading: authLoading } = useAuthGuard()
  const navigate = useNavigate()
  const [showNameCompletion, setShowNameCompletion] = useState(false)
  // 🧮 Stale-While-Revalidate: Önbellekten anında göster, arkaplanda tazele.
  // Sayfa geçişlerinde "boş ekran" flaşını tamamen engeller.
  const cached = Abacus.getCachedDashboardData()
  const [user, setUser] = useState<User | null>(cached?.user ?? null)
  const [groups, setGroups] = useState<Group[]>((cached?.groups as any) ?? [])
  const [rates, setRates] = useState<DashboardRates | null>(cached?.marketRates ?? null)
  const [loading, setLoading] = useState(!cached) // Önbellek varsa loading=false
  const [greeting, setGreeting] = useState<string>('')
  const [visibleWidgets, setVisibleWidgets] = useState<string[]>(cached?.visibleWidgets ?? [])
  const [agenda, setAgenda] = useState<AgendaWidgetData | null>(cached?.agenda ?? null)
  const [upcomingMeeting, setUpcomingMeeting] = useState<UpcomingMeetingData | null>(
    cached?.upcomingMeeting ?? null
  )
  const [isWidgetSelectorOpen, setIsWidgetSelectorOpen] = useState(false)
  const [pendingApprovals, setPendingApprovals] = useState<any[]>([])
  const [pendingSent, setPendingSent] = useState<any[]>([])
  const [showInfoPopover, setShowInfoPopover] = useState<'streak' | 'score' | null>(null)
  const [showRewardOverlay, setShowRewardOverlay] = useState(false)
  const [unpaidDebts, setUnpaidDebts] = useState<Array<{
    groupId: string; groupName: string; receiverName: string;
    amount: number; currency: string; dueDate: string; daysOverdue: number;
    currentCycle: number;
  }>>([])
  const [missingIbanAlert, setMissingIbanAlert] = useState<boolean>(false)

  useEffect(() => {
    const hour = new Date().getHours()
    if (hour >= 6 && hour < 11) setGreeting('Günaydın')
    else if (hour >= 11 && hour < 17) setGreeting('İyi günler')
    else if (hour >= 17 && hour < 22) setGreeting('İyi akşamlar')
    else setGreeting('İyi geceler')
  }, [])

  useEffect(() => {
    const controller = new AbortController()

    // 🧮 Tüm veri çekme ve hesaplama işleri Abacus Data Controller'a devredildi.
    // Dashboard sadece UI katmanı olarak çalışır.
    // Önbellekten zaten gösterim yapılıyorsa, arkaplanda sessizce tazeleme yapar.
    const loadDashboardData = async () => {
      if (!authUser?.id) return

      try {
        // Premium UX Delay: Minimum 100ms bekleme
        const [dashboardData] = await Promise.all([
          Abacus.getDashboardData(authUser.id, controller.signal),
          Abacus.premiumDelay(100),
        ])

        if (controller.signal.aborted) return

        setUser(dashboardData.user)
        setGroups(dashboardData.groups as any)
        setRates(dashboardData.marketRates)
        setVisibleWidgets(dashboardData.visibleWidgets)
        setAgenda(dashboardData.agenda)
        setUpcomingMeeting(dashboardData.upcomingMeeting)

        // 1. Borçları hesapla
        const calculatedDebts = calculateUnpaidDebts({
          groups: dashboardData.groups,
          transactions: dashboardData.transactions,
          userId: authUser.id,
          today: new Date(),
        })
        setUnpaidDebts(calculatedDebts)

        // 2. Bekleyen onay ve gönderimleri türet (groups.status === 'active' filtreli)
        const activeGroupsMap = new Map(
          (dashboardData.groups || [])
            .filter((g: any) => g.status === 'active')
            .map((g: any) => [g.id, { name: g.name, status: g.status }])
        )

        const allTransactions = dashboardData.transactions || []
        const received = allTransactions
          .filter((tx: any) => tx.status === 'pending' && tx.receiver_id === authUser.id && activeGroupsMap.has(tx.group_id))
          .map((tx: any) => ({
            id: tx.id,
            group_id: tx.group_id,
            groups: activeGroupsMap.get(tx.group_id)!,
          }))

        const sent = allTransactions
          .filter((tx: any) => tx.status === 'pending' && tx.sender_id === authUser.id && activeGroupsMap.has(tx.group_id))
          .map((tx: any) => ({
            id: tx.id,
            group_id: tx.group_id,
            groups: activeGroupsMap.get(tx.group_id)!,
          }))

        setPendingApprovals(received)
        setPendingSent(sent)

        // 3. Eksik IBAN uyarısı (Eğer sırası gelmişse ve IBAN'ı yoksa acil uyarı göster)
        const hasIbans = dashboardData.user?.ibans && dashboardData.user.ibans.length > 0
        if (hasIbans) {
          setMissingIbanAlert(false)
        } else {
          const isReceiver = (dashboardData.groups || []).some((g: any) => {
            if (g.status !== 'active' || !g.current_cycle || g.current_cycle <= 0) return false
            const members = g.group_members || []
            const myMember = members.find((m: any) => m.user_id === authUser.id)
            return myMember && myMember.turn_index === g.current_cycle
          })
          setMissingIbanAlert(Boolean(isReceiver))
        }

        // Kopma / Geri kazanım kontrolü
        const pendingInviteCode = localStorage.getItem('pending_invite_code')
        if (pendingInviteCode) {
          const userProfile = dashboardData.user
          if (userProfile?.full_name && userProfile.full_name.trim().length >= 3) {
            navigate(`/invite/${pendingInviteCode}?returnTo=auto_accept`, { replace: true })
          } else {
            setShowNameCompletion(true)
          }
        }
        
        // Show referral reward celebration if needed
        if (dashboardData.user?.has_unseen_pro_reward) {
          setShowRewardOverlay(true)
        }
      } catch (error: any) {
        if (error.name === 'AbortError' || (error.message && error.message.includes('Fetch is aborted'))) {
          return
        }
        logError('[Dashboard] Veri yükleme hatası:', error)
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    loadDashboardData()

    return () => {
      controller.abort()
    }
  }, [authUser?.id])


  // 🧮 Widget düzenleme mantığı
  const handleToggleWidget = async (widgetId: string) => {
    if (!authUser?.id) return

    let newWidgets: string[]
    if (visibleWidgets.includes(widgetId)) {
      // En az bir widget kalmalı
      if (visibleWidgets.length <= 1) return
      newWidgets = visibleWidgets.filter(id => id !== widgetId)
    } else {
      // Maksimum limit (isteğe bağlı, list view olduğu için 7 kura kadar izin verebiliriz ama mockup'a sadık kalalım)
      if (visibleWidgets.length >= 7) return
      // Seçileni sona ekle
      newWidgets = [...visibleWidgets, widgetId]
    }

    setVisibleWidgets(newWidgets)
    await saveWidgetPreferences(newWidgets)
  }

  const handleReorderWidgets = async (newOrder: string[]) => {
    if (!authUser?.id) return
    setVisibleWidgets(newOrder)
    // Sıralama değiştiğinde veritabanına kaydet
    await saveWidgetPreferences(newOrder)
  }

  const saveWidgetPreferences = async (widgets: string[]) => {
    if (!authUser?.id) return

    try {
      const { data: prefs } = await getPreferences(authUser.id)
      const currentPrefs = (prefs || {}) as Record<string, any>
      await savePreferences(authUser.id, {
        ...currentPrefs,
        dashboard_widgets: widgets.map((id, index) => ({
          id,
          visible: true,
          order: index,
        })),
      })
    } catch (error) {
      logError('[Dashboard] Tercih kaydedilemedi:', error)
    }
  }

  // Hem verilerin hem de kullanıcı bilgisinin yüklenmesini bekle

  const isPageLoading = loading || authLoading

  if (isPageLoading) {
    const loadingFirstName = authUser?.user_metadata?.full_name?.split(' ')[0] || '...'
    const loadingFullName = authUser?.user_metadata?.full_name || loadingFirstName

    const getGreeting = () => {
      const hour = new Date().getHours()
      if (hour >= 6 && hour < 12) return 'Günaydın,'
      if (hour >= 12 && hour < 18) return 'İyi günler,'
      if (hour >= 18 && hour < 24) return 'İyi akşamlar,'
      return 'İyi geceler,'
    }

    return (
      <AppLayout>
        <div className="bg-[#FCFAF8] min-h-screen font-sans">
          {/* Header - Gerçek Veri ile (Groups standardı) */}
          <header className="flex items-center justify-between px-6 pt-[max(env(safe-area-inset-top),_1.25rem)] pb-4 sticky top-0 bg-[#FCFAF8]/80 backdrop-blur-md z-40 transition-all duration-300">
            <div className="flex items-center gap-3.5">
              <div className="w-[58px] h-[58px] rounded-full border-[2.5px] border-[#D4AF37] p-[3px] flex items-center justify-center">
                <UserAvatar
                  user={authUser?.user_metadata as any}
                  size="custom"
                  className="w-full h-full"
                />
              </div>
              <div className="flex flex-col justify-center">
                <p className="text-[13px] text-[#CFA02E] font-bold tracking-wide mb-0.5">
                  {getGreeting()}
                </p>
                <h1 className="text-[22px] font-sans font-extrabold leading-tight text-espresso tracking-tight pb-0.5">
                  {loadingFullName}
                </h1>
              </div>
            </div>
            {/* Sözünün Eri Badge Skeleton or Static (Dashboard'da loading iken anonim durmasın) */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 bg-[#EDF5F1] border border-[#DCE8E3] px-3.5 py-2 rounded-full">
                <ShieldCheck size={20} className="text-[#225240]" weight="fill" />
                <span className="text-[14px] font-bold text-[#1E503F] tracking-tight">
                  {cached?.user?.reputation_score ?? '...'}
                </span>
              </div>
            </div>
          </header>

          <motion.div 
            className="px-6 space-y-7 pt-4 pb-32"
            variants={listVariants}
            initial="hidden"
            animate="show"
          >
            {/* Agenda Skeleton */}
            <motion.section variants={itemVariants}>
              <div className="flex items-center justify-between mb-4 px-1">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-32 rounded-full" />
              </div>
              <Skeleton className="h-[140px] rounded-[24px] border border-[#EAE5D9]" />
            </motion.section>

            {/* Market Summary Skeleton */}
            <motion.section variants={itemVariants} className="mb-8 space-y-4">
              <div className="flex items-center justify-between px-1">
                <Skeleton className="h-4 w-24 rounded-full" />
                <Skeleton className="h-7 w-20 rounded-full" />
              </div>
              <div className="bg-white p-3 rounded-[28px] border border-espresso/5 shadow-sm space-y-1">
                {[1, 2, 3, 4].map(i => (
                  <div
                    key={i}
                    className="grid grid-cols-[28px_1fr_auto_60px] items-center gap-3 p-2 px-4"
                  >
                    <Skeleton className="w-5 h-5 rounded-md" />
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-10 justify-self-end" />
                  </div>
                ))}
              </div>
            </motion.section>
            
            {/* Active Groups Skeleton */}
            <motion.section variants={itemVariants} className="space-y-4">
              <div className="flex items-center justify-between px-1">
                <Skeleton className="h-4 w-28 rounded-full" />
              </div>
              <Skeleton className="h-[80px] rounded-[24px] border border-[#EAE5D9]" />
              <Skeleton className="h-[80px] rounded-[24px] border border-[#EAE5D9]" />
            </motion.section>
          </motion.div>
        </div>
      </AppLayout>
    )
  }

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour >= 6 && hour < 12) return 'Günaydın,'
    if (hour >= 12 && hour < 18) return 'İyi günler,'
    if (hour >= 18 && hour < 24) return 'İyi akşamlar,'
    return 'İyi geceler,'
  }



  return (
    // Using custom inline hex code #FCFAF8 to precisely match the warm ivory/cream background of the new mockup
    <div className="bg-[#FCFAF8] min-h-screen font-sans text-espresso pb-40">
      <SEOHead title="Ana Sayfa" description="Günüm Var! ana sayfa — ajanda, piyasa takibi ve birikim gruplarınız." />
      <DashboardHeader 
        user={user} 
        getGreeting={getGreeting} 
        setShowInfoPopover={setShowInfoPopover} 
      />

        {/* ─── Info Popover ─── */}
        {showInfoPopover && (
          <>
            <div
              className="fixed inset-0 bg-black/30 z-[60]"
              onClick={() => setShowInfoPopover(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.96 }}
              transition={{ type: 'spring', damping: 28, stiffness: 350 }}
              className="fixed top-20 right-4 left-4 sm:left-auto sm:w-[300px] bg-white rounded-[28px] shadow-2xl p-6 z-[61]"
            >
              {showInfoPopover === 'streak' ? (
                <div className="text-center">
                  <div className="text-[36px] leading-none mb-1">🔥</div>
                  <p className="text-[11px] font-bold text-stone-400 uppercase tracking-widest mb-1">Ödeme Serisi</p>
                  <p className="text-[32px] font-black text-espresso leading-none">{user?.current_streak || 0}</p>
                  <p className="text-[12px] text-stone-400 mt-3 leading-relaxed">
                    Arka arkaya zamanında yaptığınız ödeme sayısı.
                    Seri arttıkça bonus puan kazanırsınız.
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <ShieldCheck size={36} className="text-[#225240] mx-auto mb-1" weight="fill" />
                  <p className="text-[11px] font-bold text-stone-400 uppercase tracking-widest mb-1">Güven Puanı</p>
                  <p className="text-[32px] font-black text-espresso leading-none">
                    {user?.reputation_score ?? 70}<span className="text-[18px] text-stone-300 font-bold">/100</span>
                  </p>
                  <p className="text-[12px] text-stone-400 mt-3 leading-relaxed">
                    Zamanında ödeme yaparak puanınızı yükseltebilirsiniz.
                    Gecikmeler puanı düşürür.
                  </p>
                </div>
              )}
              <button
                onClick={() => setShowInfoPopover(null)}
                className="mt-5 w-full py-3 bg-gradient-to-r from-[#B8922C] via-[#A07D24] to-[#8B6914] text-white text-[12px] font-black rounded-full active:scale-95 transition-all uppercase tracking-widest shadow-md shadow-[#B8922C]/25"
              >
                Anladım
              </button>
            </motion.div>
          </>
        )}

      <main className="px-6 space-y-7">
        
        {/* ─── Acil Uyarı (Eksik IBAN) ─── */}
        <MissingIbanAlert isVisible={missingIbanAlert} />

        {/* Ajanda (Agenda) Section */}
        <AgendaWidget agenda={agenda} upcomingMeeting={upcomingMeeting} />

        {/* ─── Ödeme Durumu (Borçlu Farkındalık) ─── */}
        <UnpaidDebtsWidget unpaidDebts={unpaidDebts} />

        {/* Piyasa Özeti (Market Summary) */}
        <MarketList
          rates={rates}
          visibleWidgets={visibleWidgets}
          userGroups={groups}
          onEdit={() => setIsWidgetSelectorOpen(true)}
        />

        {/* Bekleyen İşlemler */}
        <PendingTransactionsWidget 
          pendingApprovals={pendingApprovals} 
          pendingSent={pendingSent} 
        />

        {/* Aktif Günlerin (Active Groups) List */}
        <ActiveGroupsWidget groups={groups} />

        {/* Güven Puanın (Trust Score) Banner */}
        <div
          className="bg-[#EDF5F1] border border-[#DCE8E3] p-4 rounded-[20px] flex items-center justify-between cursor-pointer mt-2"
          onClick={() => navigate('/profile?section=history')}
        >
          <div className="flex items-center gap-4">
            <div className="w-[52px] h-[52px] bg-[#DFEBE5] rounded-[16px] flex items-center justify-center shrink-0">
              <ShieldCheck size={28} className="text-[#225240]" weight="fill" />
            </div>
            <div className="flex flex-col gap-0.5">
              <p className="text-[11px] font-bold text-[#80A192] uppercase tracking-[0.05em] line-clamp-1">
                {Abacus.getReputationMessage(user?.reputation_score ?? 70).text}
              </p>
              <p className="text-[16px] font-bold text-[#1E503F] tracking-tight">
                {user?.reputation_score || 70} / 100 Puan
              </p>
            </div>
          </div>
          <Info size={32} className="text-[#ABC2B6]" weight="fill" />
        </div>

        {/* 📢 Reklam Alanı — Free kullanıcılar için */}
        <AdBanner slot="9967660058" className="mt-4" />
      </main>

      <BottomNav />

      {/* Top accent gradient bar */}
      <div className="fixed top-0 left-0 w-full h-[6px] bg-gradient-to-r from-[#D4AF37] via-[#F1D279] to-[#CFA02E] z-50"></div>

      {/* Widget Selector Modal */}
      <WidgetSelector
        isOpen={isWidgetSelectorOpen}
        onClose={() => setIsWidgetSelectorOpen(false)}
        selectedWidgets={visibleWidgets}
        onToggleWidget={handleToggleWidget}
        onReorderWidgets={handleReorderWidgets}
      />

      <ProfileCompletionModal
        isOpen={showNameCompletion}
        onClose={() => setShowNameCompletion(false)}
        onComplete={() => {
          setShowNameCompletion(false)
          const pending = localStorage.getItem('pending_invite_code')
          if (pending) {
            navigate(`/invite/${pending}?returnTo=auto_accept`, { replace: true })
          }
        }}
        onCancel={() => {
          localStorage.removeItem('pending_invite_code')
          setShowNameCompletion(false)
        }}
      />
    </div>
  )
}
